import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Package, 
  Search,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { fetchStockMovements } from '../../api/stockMovements';
import styles from './StockMovements.module.scss';
import logger from '../../utils/logger';

const StockMovements = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadData();
  }, []); // Only load data once on component mount

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load all stock movements by fetching in chunks
      let allMovements = [];
      let skip = 0;
      const limit = 1000; // API max limit
      let hasMore = true;
      
      // Fetch all movements in chunks
      while (hasMore) {
        try {
          setLoadingProgress(`Loading movements... (${allMovements.length} loaded)`);
          const chunk = await fetchStockMovements(skip, limit);
          if (chunk && chunk.length > 0) {
            allMovements = [...allMovements, ...chunk];
            skip += limit;
            hasMore = chunk.length === limit; // Continue if we got a full chunk
          } else {
            hasMore = false;
          }
        } catch (chunkError) {
          logger.error('Error fetching chunk:', chunkError);
          // If it's a 422 error, it might be because we're trying to fetch beyond available data
          if (chunkError.message && chunkError.message.includes('422')) {
            hasMore = false; // Stop trying to fetch more
          } else {
            hasMore = false;
          }
        }
      }
      
      setMovements(allMovements);
      setError(null);
      setLoadingProgress('');
    } catch (err) {
      logger.error('Error loading data:', err);
      setError(err.message || 'Failed to load stock movements');
      setLoadingProgress('');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatQuantity = (quantity, unit) => {
    return `${parseFloat(quantity).toFixed(3)} ${unit}`;
  };

  const getMovementIcon = (changeType) => {
    return changeType === 'INCREASE' ? TrendingUp : TrendingDown;
  };

  const getMovementColor = (changeType) => {
    return changeType === 'INCREASE' ? '#28a745' : '#dc3545';
  };

  const getMovementText = (changeType) => {
    return changeType === 'INCREASE' ? 'Increased' : 'Decreased';
  };

  const filteredMovements = useMemo(() => {
    return movements.filter(movement => {
      if (search) {
        const searchLower = search.toLowerCase();
        return (
          movement.chemical?.name?.toLowerCase().includes(searchLower) ||
          movement.action?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [movements, search]);

  const paginatedMovements = filteredMovements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{loadingProgress || 'Loading stock movements...'}</p>
      </div>
    );
  }

  return (
    <div className={styles.stockMovements}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2>Stock Movements</h2>
          <p>Track all chemical stock changes and movements</p>
        </div>
        <button 
          className={styles.refreshButton}
          onClick={loadData}
          disabled={loading}
        >
          <RefreshCw size={20} className={loading ? styles.spinning : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className={styles.error}>
          <AlertCircle size={20} />
          <span>{typeof error === 'string' ? error : JSON.stringify(error)}</span>
        </div>
      )}

      <div className={styles.searchSection}>
        <div className={styles.searchInput}>
          <Search size={16} />
          <input
            type="text"
            placeholder="Search movements..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.movementsTable}>
        {paginatedMovements.length === 0 ? (
          <div className={styles.emptyState}>
            <Package size={48} />
            <h3>No movements found</h3>
            <p>
              {search 
                ? 'No movements match your search'
                : 'No stock movements have been recorded yet'
              }
            </p>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Chemical</th>
                  <th>Type</th>
                  <th>Quantity Changed</th>
                  <th>Action</th>
                  <th>Reference</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMovements.map((movement) => {
                  const MovementIcon = getMovementIcon(movement.change_type);
                  const movementColor = getMovementColor(movement.change_type);
                  const movementText = getMovementText(movement.change_type);

                  return (
                    <tr key={movement.id} className={styles.tableRow}>
                      <td className={styles.chemicalCell}>
                        <div className={styles.chemicalInfo}>
                          <Package size={16} />
                          <span>{movement.chemical?.name || 'Unknown Chemical'}</span>
                        </div>
                      </td>
                      <td className={styles.typeCell}>
                        <div className={styles.movementType}>
                          <MovementIcon 
                            size={16} 
                            style={{ color: movementColor }}
                          />
                          <span style={{ color: movementColor }}>
                            {movementText}
                          </span>
                        </div>
                      </td>
                      <td className={styles.quantityCell}>
                        <span 
                          className={styles.quantityValue}
                          style={{ color: movementColor }}
                        >
                          {movement.change_type === 'INCREASE' ? '+' : '-'}
                          {formatQuantity(movement.quantity_changed, movement.unit)}
                        </span>
                      </td>
                      <td className={styles.actionCell}>
                        <span className={styles.actionValue}>{movement.action}</span>
                      </td>
                      <td className={styles.referenceCell}>
                        {movement.reference_type ? (
                          <span className={styles.referenceValue}>
                            {movement.reference_type} #{movement.reference_id}
                          </span>
                        ) : (
                          <span className={styles.noReference}>-</span>
                        )}
                      </td>
                      <td className={styles.dateCell}>
                        <div className={styles.dateInfo}>
                          <Calendar size={14} />
                          <span>{formatDate(movement.timestamp)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filteredMovements.length > itemsPerPage && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <span className={styles.pageInfo}>
            Page {currentPage} of {Math.ceil(filteredMovements.length / itemsPerPage)}
          </span>
          
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredMovements.length / itemsPerPage)))}
            disabled={currentPage >= Math.ceil(filteredMovements.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      )}

    </div>
  );
};

export default StockMovements;
